<template>
    <div class="options-page">
        <PageSpinner :onloader="onloader" />
        <base-header type="gradient-info" class="pb-6 pb-8 pt-5 pt-md-8">
            <router-link to="/dashboard" class="text-white">
                Dashboard
            </router-link>
            <router-link to="/school/academic-management" class="text-white">
                <i class="fa fa-ellipsis-v px-2"></i>Academic Management
            </router-link>
            <span class="text-white"><i class="fa fa-ellipsis-v px-2"></i>ClassOptions</span>
        </base-header>

        <div class="container-fluid mt--7 options-page__body">
            <section class="options-panel" aria-labelledby="options-page-title">
                <header class="options-panel__intro">
                    
                    <div class="options-panel__side">
                        <div
                            v-if="selectedClassId && optionitems !== null"
                            class="options-stat"
                        >
                            <strong>{{ options.length }}</strong>
                            <span>{{ options.length === 1 ? "option" : "options" }}</span>
                        </div>
                        <button
                            type="button"
                            class="btn options-add"
                            :disabled="!classes.length"
                            :title="classes.length ? 'Register a new option' : 'Create a class first'"
                            @click="openCreate"
                        >
                            <i class="fa fa-plus" aria-hidden="true"></i>
                            <span>New option</span>
                        </button>
                    </div>
                </header>

                <header class="options-toolbar">
                    <div class="options-toolbar__title">
                        <h2>Registered options</h2>
                        <p class="options-toolbar__meta">{{ scopeLabel }}</p>
                    </div>

                    <div class="options-toolbar__tools">
                        <label class="options-scope">
                            <span>Class</span>
                            <select
                                v-model="selectedClassId"
                                class="options-scope__select"
                                :disabled="classesLoading || !classes.length"
                                aria-label="Class to show options for"
                                @change="onClassChange"
                            >
                                <option v-if="classesLoading" :value="0" disabled>
                                    Loading classes…
                                </option>
                                <option v-else :value="0">Select class</option>
                                <option v-for="item in classes" :key="item.Id" :value="item.Id">
                                    {{ item.Sch_Class }}
                                </option>
                            </select>
                        </label>

                        <div class="options-search">
                            <i class="fa fa-search" aria-hidden="true"></i>
                            <input
                                v-model="searchval"
                                type="search"
                                class="options-search__input"
                                placeholder="Search options"
                                aria-label="Search options for the selected class"
                                :disabled="!options.length"
                            />
                            <button
                                v-if="searchval"
                                type="button"
                                class="options-search__clear"
                                aria-label="Clear search"
                                @click="searchval = ''"
                            >
                                <i class="fa fa-times" aria-hidden="true"></i>
                            </button>
                        </div>
                    </div>
                </header>

                <div v-if="classesLoading" class="options-state">
                    <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
                    <strong>Loading classes</strong>
                    <span>Fetching the classes available at your school.</span>
                </div>

                <div v-else-if="classesFailed" class="options-state">
                    <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
                    <strong>Classes could not be loaded</strong>
                    <span>Check your connection and try again.</span>
                    <button type="button" class="btn options-ghost-button" @click="retryClasses">
                        <i class="fa fa-refresh" aria-hidden="true"></i>
                        <span>Try again</span>
                    </button>
                </div>

                <div v-else-if="!classes.length" class="options-state">
                    <i class="fa fa-graduation-cap" aria-hidden="true"></i>
                    <strong>No classes available</strong>
                    <span>Create classes before registering the options for them.</span>
                    <router-link
                        to="/school/academic-management/classes"
                        class="btn options-ghost-button"
                    >
                        <i class="fa fa-arrow-right" aria-hidden="true"></i>
                        <span>Go to classes</span>
                    </router-link>
                </div>

                <div v-else-if="!selectedClassId" class="options-state">
                    <i class="fa fa-filter" aria-hidden="true"></i>
                    <strong>Select a class</strong>
                    <span>Choose a class above to see and manage its options.</span>
                </div>

                <div v-else-if="optionsFailed" class="options-state">
                    <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
                    <strong>Options could not be loaded</strong>
                    <span>Try loading the options for {{ selectedClassName }} again.</span>
                    <button type="button" class="btn options-ghost-button" @click="retryOptions">
                        <i class="fa fa-refresh" aria-hidden="true"></i>
                        <span>Try again</span>
                    </button>
                </div>

                <div v-else-if="optionsLoading && optionitems === null" class="options-state">
                    <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
                    <strong>Loading options</strong>
                    <span>Fetching options for {{ selectedClassName }}.</span>
                </div>

                <div v-else-if="!options.length" class="options-state">
                    <i class="fa fa-list-ul" aria-hidden="true"></i>
                    <strong>No options for {{ selectedClassName }}</strong>
                    <span>Register an option such as Science, Arts or Commercial.</span>
                    <button type="button" class="btn options-ghost-button" @click="openCreate">
                        <i class="fa fa-plus" aria-hidden="true"></i>
                        <span>Add option</span>
                    </button>
                </div>

                <div v-else-if="!filteredOptions.length" class="options-state">
                    <i class="fa fa-search" aria-hidden="true"></i>
                    <strong>No options match “{{ searchval.trim() }}”</strong>
                    <span>Try a different option name.</span>
                    <button type="button" class="btn options-ghost-button" @click="searchval = ''">
                        Clear search
                    </button>
                </div>

                <div v-else class="options-grid" :class="{ 'is-loading': optionsLoading }">
                    <article
                        v-for="(item, index) in filteredOptions"
                        :key="rowKey(item, index)"
                        class="option-card"
                    >
                        <span class="option-card__index">{{ String(index + 1).padStart(2, "0") }}</span>
                        <span class="option-card__name">{{ item.ClassOption || "—" }}</span>
                        <button
                            type="button"
                            class="option-card__edit"
                            :aria-label="editLabel(item)"
                            @click="editItem(item)"
                        >
                            <i class="fa fa-pencil" aria-hidden="true"></i>
                            <span>Edit</span>
                        </button>
                    </article>
                </div>
            </section>
        </div>

        <modal
            :show.sync="formVisible"
            modal-classes="modal-dialog-centered"
            @close="handleFormClosed"
        >
            <div class="options-drawer">
                <header class="options-drawer__head">
                    <span class="options-drawer__icon" aria-hidden="true">
                        <i class="fa" :class="editingId ? 'fa-pencil' : 'fa-list-ul'"></i>
                    </span>
                    <div class="options-drawer__heading">
                        <span class="options-drawer__eyebrow">
                            {{ editingId ? "UPDATE RECORD" : "NEW RECORD" }}
                        </span>
                        <h2>{{ editingId ? "Edit option" : "Add an option" }}</h2>
                        <p v-if="drawerLabel">{{ drawerLabel }}</p>
                        <p v-else>Choose a class and name the option or stream.</p>
                    </div>
                    <button
                        type="button"
                        class="options-drawer__close"
                        aria-label="Close"
                        @click="closeForm"
                    >
                        <i class="fa fa-times" aria-hidden="true"></i>
                    </button>
                </header>

                <form id="option-form" class="options-form" novalidate @submit.prevent="submitOption">
                    <fieldset class="options-fieldset">
                        <legend>Option details</legend>

                        <div v-if="!classes.length" class="options-notice">
                            <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
                            <span>No classes are available. Create a class before registering options.</span>
                        </div>

                        <div class="row">
                            <div class="col-sm-6">
                                <div class="form-group">
                                    <label for="option-class">
                                        Class <span class="options-required">*</span>
                                    </label>
                                    <select
                                        id="option-class"
                                        v-model="form.classId"
                                        class="form-control"
                                        :class="{ 'is-invalid': errors.classId }"
                                        :disabled="isSaving || !classes.length"
                                        @change="clearError('classId')"
                                    >
                                        <option :value="0">Select class</option>
                                        <option v-for="item in classes" :key="item.Id" :value="item.Id">
                                            {{ item.Sch_Class }}
                                        </option>
                                    </select>
                                    <span v-if="errors.classId" class="options-error" role="alert">
                                        {{ errors.classId }}
                                    </span>
                                    <span v-else class="options-hint">
                                        The class this option belongs to.
                                    </span>
                                </div>
                            </div>

                            <div class="col-sm-6">
                                <div class="form-group">
                                    <label for="option-name">
                                        Option name <span class="options-required">*</span>
                                    </label>
                                    <input
                                        id="option-name"
                                        v-model="form.optionName"
                                        type="text"
                                        class="form-control"
                                        :class="{ 'is-invalid': errors.optionName }"
                                        placeholder="e.g. Science"
                                        maxlength="80"
                                        autocomplete="off"
                                        :disabled="isSaving"
                                        @input="clearError('optionName')"
                                    />
                                    <span v-if="errors.optionName" class="options-error" role="alert">
                                        {{ errors.optionName }}
                                    </span>
                                    <span v-else class="options-hint">
                                        Shown when placing students into streams.
                                    </span>
                                </div>
                            </div>
                        </div>

                        <p class="options-fieldset__note">
                            An option can only belong to one class. Moving it here will remove
                            it from its current class.
                        </p>
                    </fieldset>
                </form>
            </div>

            <template slot="footer">
                <div class="options-drawer__actions">
                    <button
                        type="button"
                        class="btn btn-outline-secondary"
                        :disabled="isSaving"
                        @click="closeForm"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        form="option-form"
                        class="btn options-submit"
                        :disabled="isSaving || !classes.length"
                    >
                        <i
                            class="fa"
                            :class="isSaving ? 'fa-spinner fa-spin' : 'fa-check'"
                            aria-hidden="true"
                        ></i>
                        <span>{{ submitLabel }}</span>
                    </button>
                </div>
            </template>
        </modal>
    </div>
</template>

<script>
import PageSpinner from "@/components/PageSpinner";
import Alert from "@/mixins/Alert.mixins";
import modal from "@/components/Modal";

const EMPTY_FORM = () => ({ classId: 0, optionName: "" });

export default {
    name: "option",
    mixins: [Alert],
    components: {
        PageSpinner,
        modal
    },
    data() {
        return {
            accessDetails: null,
            classitems: null,
            optionitems: null,
            selectedClassId: 0,
            searchval: "",
            classesFailed: false,
            optionsFailed: false,
            optionsLoading: false,
            optionsSeq: 0,
            formVisible: false,
            isSaving: false,
            editingId: 0,
            form: EMPTY_FORM(),
            errors: {},
            onloader: "none"
        };
    },
    computed: {
        classes() {
            return Array.isArray(this.classitems) ? this.classitems : [];
        },
        options() {
            return Array.isArray(this.optionitems) ? this.optionitems : [];
        },
        classesLoading() {
            return this.classitems === null;
        },
        selectedClassName() {
            const selected = this.classes.find(
                item => String(item.Id) === String(this.selectedClassId)
            );
            return selected ? selected.Sch_Class : "";
        },
        hasSearch() {
            return this.searchval.trim().length > 0;
        },
        filteredOptions() {
            const tokens = this.searchval
                .toLowerCase()
                .split(/\s+/)
                .filter(Boolean);

            if (!tokens.length) return this.options;

            return this.options.filter(item => {
                const haystack = [item.ClassOption, item.OptionName, item.Name]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                return tokens.every(token => haystack.includes(token));
            });
        },
        scopeLabel() {
            if (this.classesLoading) return "Loading classes…";
            if (!this.classes.length) return "No classes available.";
            if (!this.selectedClassId) return "Choose a class to view its options.";
            if (this.optionsFailed) return `${this.selectedClassName} · options unavailable`;
            if (this.optionsLoading) return `${this.selectedClassName} · loading options…`;
            if (this.hasSearch) {
                return `${this.selectedClassName} · ${this.filteredOptions.length} of ${this.options.length} shown`;
            }
            const count = this.options.length;
            return `${this.selectedClassName} · ${count} ${count === 1 ? "option" : "options"}`;
        },
        formClassName() {
            const selected = this.classes.find(
                item => String(item.Id) === String(this.form.classId)
            );
            return selected ? selected.Sch_Class : "";
        },
        drawerLabel() {
            if (this.editingId) {
                return [this.form.optionName, this.formClassName].filter(Boolean).join(" · ");
            }
            return this.formClassName;
        },
        submitLabel() {
            if (this.isSaving) return "Saving…";
            return this.editingId ? "Save changes" : "Create option";
        }
    },
    methods: {
        rowKey(item, index) {
            const id = item && (item.Id || item.id);
            return id !== undefined && id !== null && id !== "" ? id : `option-${index}`;
        },
        editLabel(item) {
            return item && item.ClassOption ? `Edit ${item.ClassOption}` : "Edit option";
        },
        errorMessage(error, fallback) {
            const data = error && error.response && error.response.data;
            if (typeof data === "string" && data.trim()) return data.trim();
            if (data && typeof data === "object") {
                return data.message || data.responseMessage || fallback;
            }
            if (error && error.message) return error.message;
            return fallback;
        },
        async loadClasses() {
            this.classitems = null;
            this.classesFailed = false;

            try {
                const resp = await this.$store.dispatch("getclass");
                this.classitems = Array.isArray(resp) ? resp : [];

                const hasSelection = this.classitems.some(
                    item => String(item.Id) === String(this.selectedClassId)
                );
                if (!hasSelection) {
                    this.selectedClassId = this.classitems.length
                        ? this.classitems[0].Id
                        : 0;
                }
                if (this.selectedClassId) this.loadOptions();
            } catch (error) {
                this.classitems = [];
                this.classesFailed = true;
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to load classes at the moment."),
                    ""
                );
            }
        },
        async loadOptions(options = {}) {
            const silent = Boolean(options && options.silent);
            const classId = this.selectedClassId;
            if (!classId) {
                this.optionitems = null;
                this.optionsLoading = false;
                return;
            }

            const sequence = ++this.optionsSeq;
            this.optionsLoading = true;
            this.optionsFailed = false;

            try {
                const resp = await this.$store.dispatch("getoption", classId);
                if (sequence !== this.optionsSeq) return;
                this.optionitems = Array.isArray(resp) ? resp : [];
            } catch (error) {
                if (sequence !== this.optionsSeq) return;
                this.optionsFailed = true;
                this.optionitems = [];
                if (!silent) {
                    this.ShowAlertError(
                        this.errorMessage(error, "Unable to load options for this class."),
                        ""
                    );
                }
            } finally {
                if (sequence === this.optionsSeq) this.optionsLoading = false;
            }
        },
        retryClasses() {
            this.loadClasses();
        },
        retryOptions() {
            this.optionsFailed = false;
            this.optionitems = null;
            this.loadOptions();
        },
        onClassChange() {
            this.searchval = "";
            this.optionitems = null;
            this.optionsFailed = false;
            this.loadOptions();
        },
        resetForm() {
            this.editingId = 0;
            this.errors = {};
            this.form = EMPTY_FORM();
        },
        openCreate() {
            if (!this.classes.length) return;
            this.resetForm();
            this.form = {
                classId: this.selectedClassId || 0,
                optionName: ""
            };
            this.formVisible = true;
        },
        editItem(item) {
            if (!item) return;
            this.resetForm();
            this.editingId = item.Id || item.id || 0;
            this.form = {
                classId: item.ClassId || this.selectedClassId || 0,
                optionName: item.ClassOption || item.OptionName || ""
            };
            this.formVisible = true;
        },
        closeForm() {
            this.formVisible = false;
            this.resetForm();
        },
        handleFormClosed() {
            this.resetForm();
        },
        handleKeydown(event) {
            if (event.key !== "Escape") return;
            if (!this.formVisible || this.isSaving) return;
            this.closeForm();
        },
        clearError(field) {
            if (!this.errors[field]) return;
            const errors = { ...this.errors };
            delete errors[field];
            this.errors = errors;
        },
        validate() {
            const errors = {};
            const classId = this.form.classId;
            if (
                classId === 0 ||
                classId === "0" ||
                classId === "" ||
                classId === null ||
                classId === undefined
            ) {
                errors.classId = "Choose the class this option belongs to.";
            }

            const optionName = String(this.form.optionName || "").trim();
            if (!optionName) {
                errors.optionName = "Enter the option name.";
            }

            this.errors = errors;

            if (Object.keys(errors).length) {
                const fieldIds = {
                    classId: "option-class",
                    optionName: "option-name"
                };
                const firstField = Object.keys(errors)[0];
                const element = document.getElementById(fieldIds[firstField]);
                if (element) element.focus();
            }

            return Object.keys(errors).length === 0;
        },
        async submitOption() {
            if (this.isSaving) return;
            if (!this.validate()) return;

            const isEdit = Boolean(this.editingId);
            const classId = this.form.classId;
            const optionName = String(this.form.optionName || "").trim();
            const payload = {
                Id: this.editingId || 1,
                ClassId: classId,
                SchOptionName: optionName,
                processop: isEdit ? "U" : "N",
                username: this.$store.state.auth.username,
                accesskey: this.$store.state.auth.accesskey
            };

            this.isSaving = true;
            this.onloader = "flex";
            try {
                const resp = await this.$store.dispatch("optionregistration", payload);

                if (resp && resp.responseCode && resp.responseCode !== "00") {
                    this.ShowAlertError(
                        resp.responseMessage || "The option could not be saved.",
                        ""
                    );
                    return;
                }

                this.closeForm();
                this.searchval = "";
                if (String(this.selectedClassId) !== String(classId)) {
                    this.selectedClassId = classId;
                }
                this.optionitems = null;
                await this.loadOptions({ silent: true });

                if (this.optionsFailed) {
                    this.ShowAlertWarning(
                        "Option saved, but the updated list could not be loaded.",
                        ""
                    );
                } else {
                    this.ShowAlertSuccess(
                        isEdit ? "Option updated successfully." : "Option created successfully.",
                        ""
                    );
                }
            } catch (error) {
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to save this option at the moment."),
                    ""
                );
            } finally {
                this.isSaving = false;
                this.onloader = "none";
            }
        }
    },
    mounted() {
        const userDetails = this.$store.getters["auth/_user"];
        if (!userDetails || userDetails.isSchoolStaff == false) {
            this.$store.dispatch("auth/Logout");
            this.$router.push("/login");
            return;
        }

        document.addEventListener("keydown", this.handleKeydown);
        this.loadClasses();
    },
    beforeDestroy() {
        document.removeEventListener("keydown", this.handleKeydown);
    }
};
</script>

<style scoped>
.options-page {
    min-height: 100%;
}

.options-page__body {
    padding-bottom: 2.5rem;
}

.options-panel {
    position: relative;
    overflow: hidden;
    border: 1px solid #e5eaf0;
    border-radius: 0.9rem;
    background: #fff;
    box-shadow: 0 0.6rem 1.75rem rgba(50, 70, 90, 0.1);
}

.options-panel__intro {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 1.25rem;
    padding: 1.5rem 1.5rem 1.35rem;
    border-bottom: 1px solid #edf0f3;
}

.options-panel__text {
    min-width: 0;
}

.options-eyebrow {
    display: block;
    margin-bottom: 0.35rem;
    color: #5b86b5;
    font-size: 0.66rem;
    font-weight: 700;
    letter-spacing: 0.11em;
}

.options-panel__intro h1 {
    margin: 0;
    color: #293b52;
    font-size: 1.6rem;
    font-weight: 700;
    line-height: 1.2;
}

.options-panel__intro p {
    max-width: 48rem;
    margin: 0.45rem 0 0;
    color: #7d8b9a;
    font-size: 0.88rem;
    line-height: 1.55;
}

.options-panel__side {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 0.75rem;
}

.options-stat {
    display: flex;
    align-items: baseline;
    gap: 0.4rem;
    padding: 0.5rem 0.85rem;
    border: 1px solid #dbe7f4;
    border-radius: 0.65rem;
    background: #f5f9ff;
    color: #5b7fa6;
    font-size: 0.76rem;
    font-weight: 600;
    white-space: nowrap;
}

.options-stat strong {
    color: #1850b8;
    font-size: 1rem;
    font-weight: 700;
}

.options-add {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
    padding: 0.62rem 1.05rem;
    border: 1px solid #1850b8;
    border-radius: 0.55rem;
    background: #1850b8;
    color: #fff;
    font-size: 0.82rem;
    font-weight: 600;
    line-height: 1.2;
    box-shadow: 0 0.35rem 0.9rem rgba(24, 80, 184, 0.22);
    transition: background-color 0.15s ease, box-shadow 0.15s ease;
}

.options-add:hover:not(:disabled),
.options-add:focus:not(:disabled) {
    background: #144a9e;
    border-color: #144a9e;
    color: #fff;
    box-shadow: 0 0.45rem 1.1rem rgba(24, 80, 184, 0.28);
}

.options-add:focus-visible {
    outline: 2px solid #1850b8;
    outline-offset: 2px;
}

.options-add:disabled {
    border-color: #b9c8dc;
    background: #b9c8dc;
    box-shadow: none;
    cursor: not-allowed;
}

.options-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1.15rem 1.5rem;
    border-bottom: 1px solid #edf0f3;
}

.options-toolbar h2 {
    margin: 0;
    color: #344767;
    font-size: 1.05rem;
    font-weight: 700;
}

.options-toolbar__meta {
    margin: 0.3rem 0 0;
    color: #8490a0;
    font-size: 0.76rem;
}

.options-toolbar__tools {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.options-scope {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
    color: #7b8998;
    font-size: 0.76rem;
    font-weight: 600;
    white-space: nowrap;
}

.options-scope__select {
    height: 2.4rem;
    min-width: 175px;
    padding: 0 1.75rem 0 0.65rem;
    border: 1px solid #dbe3ea;
    border-radius: 0.5rem;
    background: #fff;
    color: #344767;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
}

.options-scope__select:focus {
    border-color: #7ea8dd;
    outline: none;
    box-shadow: 0 0 0 0.2rem rgba(24, 80, 184, 0.12);
}

.options-scope__select:disabled {
    background: #f4f6f9;
    color: #8492a2;
    cursor: not-allowed;
}

.options-search {
    position: relative;
    display: flex;
    flex: 0 1 280px;
    align-items: center;
    min-width: 210px;
}

.options-search > i {
    position: absolute;
    left: 0.8rem;
    color: #9aa6b3;
    font-size: 0.8rem;
    pointer-events: none;
}

.options-search__input {
    width: 100%;
    height: 2.4rem;
    padding: 0.4rem 2.25rem 0.4rem 2.15rem;
    border: 1px solid #dbe3ea;
    border-radius: 0.55rem;
    background: #fff;
    color: #344767;
    font-size: 0.82rem;
    -webkit-appearance: none;
}

.options-search__input::placeholder {
    color: #a2adba;
}

.options-search__input::-webkit-search-cancel-button {
    display: none;
}

.options-search__input:focus {
    border-color: #7ea8dd;
    outline: none;
    box-shadow: 0 0 0 0.2rem rgba(24, 80, 184, 0.12);
}

.options-search__input:disabled {
    background: #f4f6f9;
    color: #a2adba;
    cursor: not-allowed;
}

.options-search__clear {
    position: absolute;
    right: 0.4rem;
    display: inline-flex;
    width: 1.75rem;
    height: 1.75rem;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: #9aa6b3;
    font-size: 0.72rem;
    cursor: pointer;
}

.options-search__clear:hover,
.options-search__clear:focus {
    background: #eef2f6;
    color: #526273;
    outline: none;
}

.options-state {
    display: flex;
    min-height: 240px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    padding: 2rem 1.5rem;
    color: #83909e;
    text-align: center;
}

.options-state > i {
    margin-bottom: 0.35rem;
    color: #9db6d4;
    font-size: 1.6rem;
}

.options-state strong {
    color: #526273;
    font-size: 0.95rem;
}

.options-state span {
    max-width: 28rem;
    font-size: 0.8rem;
    line-height: 1.55;
}

.options-state .options-ghost-button {
    margin-top: 0.75rem;
}

.options-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 0.85rem;
    padding: 1.25rem 1.5rem;
    transition: opacity 0.15s ease;
}

.options-grid.is-loading {
    opacity: 0.5;
    pointer-events: none;
}

.option-card {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 0.8rem;
    padding: 0.85rem 0.9rem;
    border: 1px solid #e5eaf0;
    border-radius: 0.75rem;
    background: #fff;
    box-shadow: 0 0.25rem 0.75rem rgba(50, 70, 90, 0.05);
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.option-card:hover {
    border-color: #bcd4ef;
    box-shadow: 0 0.4rem 1rem rgba(24, 80, 184, 0.1);
}

.option-card__index {
    display: grid;
    flex: 0 0 2.2rem;
    width: 2.2rem;
    height: 2.2rem;
    place-items: center;
    border-radius: 0.55rem;
    background: #eef4ff;
    color: #2d6fae;
    font-size: 0.68rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
}

.option-card__name {
    min-width: 0;
    flex: 1 1 auto;
    overflow-wrap: anywhere;
    color: #344767;
    font-size: 0.92rem;
    font-weight: 700;
}

.option-card__edit {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 0.35rem;
    padding: 0.32rem 0.55rem;
    border: 1px solid #dbe3ea;
    border-radius: 0.45rem;
    background: #fff;
    color: #44566c;
    font-size: 0.73rem;
    font-weight: 600;
    cursor: pointer;
    transition: border-color 0.15s ease, color 0.15s ease, background-color 0.15s ease;
}

.option-card__edit:hover,
.option-card__edit:focus {
    border-color: #9dbfe6;
    background: #f5f9ff;
    color: #1850b8;
    outline: none;
}

.options-ghost-button {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    margin: 0;
    padding: 0.4rem 0.7rem;
    border: 1px solid #dbe3ea;
    border-radius: 0.45rem;
    background: #fff;
    color: #44566c;
    font-size: 0.76rem;
    font-weight: 600;
    line-height: 1.3;
    transition: border-color 0.15s ease, color 0.15s ease, background-color 0.15s ease;
}

.options-ghost-button:hover,
.options-ghost-button:focus {
    border-color: #9dbfe6;
    background: #f5f9ff;
    color: #1850b8;
    outline: none;
}

/* ---------- Drawer form ---------- */

.options-drawer {
    display: flex;
    min-height: 100%;
    flex-direction: column;
}

.options-drawer__head {
    display: flex;
    align-items: flex-start;
    gap: 0.85rem;
    padding-bottom: 1.1rem;
    border-bottom: 1px solid #edf0f3;
}

.options-drawer__icon {
    display: grid;
    flex: 0 0 2.6rem;
    width: 2.6rem;
    height: 2.6rem;
    place-items: center;
    border-radius: 0.7rem;
    background: #eef4ff;
    color: #1850b8;
    font-size: 1rem;
}

.options-drawer__heading {
    min-width: 0;
}

.options-drawer__eyebrow {
    display: block;
    margin-bottom: 0.15rem;
    color: #5b86b5;
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.1em;
}

.options-drawer__heading h2 {
    margin: 0;
    color: #344767;
    font-size: 1.15rem;
    font-weight: 700;
    line-height: 1.25;
}

.options-drawer__heading p {
    margin: 0.3rem 0 0;
    color: #7d8b9a;
    font-size: 0.8rem;
    line-height: 1.45;
}

.options-drawer__close {
    display: inline-flex;
    flex: 0 0 2rem;
    width: 2rem;
    height: 2rem;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: #8492a2;
    font-size: 0.85rem;
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease;
}

.options-drawer__close:hover,
.options-drawer__close:focus {
    background: #eef2f6;
    color: #344767;
    outline: none;
}

.options-form {
    margin: 0;
}

.options-form .row {
    margin-right: -7.5px;
    margin-left: -7.5px;
}

.options-form .row > [class*="col-"] {
    padding-right: 7.5px;
    padding-left: 7.5px;
}

.options-fieldset {
    margin: 1.35rem 0 0;
    padding: 0;
    border: 0;
}

.options-fieldset legend {
    display: block;
    width: auto;
    margin: 0 0 0.85rem;
    padding: 0;
    color: #5b86b5;
    font-size: 0.64rem;
    font-weight: 700;
    letter-spacing: 0.09em;
    text-transform: uppercase;
}

.options-fieldset__note {
    margin: 0.35rem 0 0;
    color: #8a97a6;
    font-size: 0.75rem;
    line-height: 1.5;
}

.options-notice {
    display: flex;
    align-items: flex-start;
    gap: 0.55rem;
    margin-bottom: 1rem;
    padding: 0.7rem 0.8rem;
    border: 1px solid #f0d9aa;
    border-radius: 0.6rem;
    background: #fff8e9;
    color: #77551d;
    font-size: 0.76rem;
    line-height: 1.5;
}

.options-form .form-group {
    margin-bottom: 1rem;
}

.options-form label {
    margin-bottom: 0.35rem;
    color: #44566c;
    font-size: 0.78rem;
    font-weight: 700;
}

.options-required {
    color: #c23b4a;
    font-weight: 700;
}

.options-form .form-control {
    height: 2.5rem;
    padding: 0.35rem 0.7rem;
    border: 1px solid #dbe3ea;
    border-radius: 0.5rem;
    background: #fff;
    color: #344767;
    font-size: 0.84rem;
}

.options-form .form-control:focus {
    border-color: #7ea8dd;
    outline: none;
    box-shadow: 0 0 0 0.2rem rgba(24, 80, 184, 0.12);
}

.options-form .form-control:disabled {
    background: #f4f6f9;
    color: #8492a2;
    cursor: not-allowed;
}

.options-form .form-control.is-invalid {
    border-color: #d76a76;
    box-shadow: 0 0 0 0.2rem rgba(194, 59, 74, 0.1);
}

.options-error {
    display: block;
    margin-top: 0.3rem;
    color: #c23b4a;
    font-size: 0.72rem;
    font-weight: 600;
}

.options-hint {
    display: block;
    margin-top: 0.3rem;
    color: #8a97a6;
    font-size: 0.72rem;
    line-height: 1.45;
}

.options-drawer__actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.6rem;
    width: 100%;
}

.options-drawer__actions .btn {
    display: inline-flex;
    min-height: 2.5rem;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    margin: 0;
    padding: 0.4rem 1.1rem;
    border-radius: 0.5rem;
    font-size: 0.82rem;
    font-weight: 600;
}

.options-submit {
    border: 1px solid #1850b8;
    background: #1850b8;
    color: #fff;
}

.options-submit:hover,
.options-submit:focus {
    border-color: #144a9e;
    background: #144a9e;
    color: #fff;
}

.options-submit:disabled {
    border-color: #b9c8dc;
    background: #b9c8dc;
    color: #fff;
    cursor: not-allowed;
}

@media (max-width: 767.98px) {
    .options-panel__intro {
        align-items: flex-start;
    }

    .options-toolbar {
        flex-direction: column;
        align-items: stretch;
    }

    .options-toolbar__tools {
        flex-wrap: wrap;
    }

    .options-search {
        flex: 1 1 100%;
        width: 100%;
    }
}

@media (max-width: 575.98px) {
    .options-page__body {
        padding-bottom: 1.5rem;
    }

    .options-panel__intro {
        flex-direction: column;
        gap: 1rem;
        padding: 1.15rem 1rem 1rem;
    }

    .options-panel__intro h1 {
        font-size: 1.35rem;
    }

    .options-panel__side {
        width: 100%;
        flex-wrap: wrap;
    }

    .options-stat {
        flex: 1 1 auto;
    }

    .options-add {
        flex: 1 1 auto;
        justify-content: center;
    }

    .options-toolbar {
        padding: 1rem;
    }

    .options-toolbar__tools {
        align-items: stretch;
        flex-direction: column;
        gap: 0.65rem;
    }

    .options-scope {
        justify-content: space-between;
    }

    .options-scope__select {
        flex: 0 0 auto;
        min-width: 0;
        max-width: 62%;
    }

    .options-search {
        flex: 0 0 auto;
        min-width: 0;
    }

    .options-grid {
        padding: 0.85rem;
        grid-template-columns: minmax(0, 1fr);
    }

    .options-state {
        padding: 1.5rem 1rem;
    }

    .options-drawer__head {
        gap: 0.7rem;
    }

    .options-drawer__actions {
        flex-direction: column-reverse;
    }

    .options-drawer__actions .btn {
        width: 100%;
    }
}
</style>
